import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft, 
  Plus, 
  Users, 
  Table,
  CheckCircle,
  Clock,
  AlertTriangle,
  LogOut,
  UserPlus,
  BarChart3,
  Smartphone,
  Upload,
  FileText,
  X,
  Check,
  Edit,
  Trash2,
  Search,
  Eye,
  Copy
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const EventDetails = ({ user, onLogout }) => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  
  // Helper function to check if user has admin/edit access to tischplan
  const canManageTischplan = () => {
    if (!user) return false;
    // Super admin and admin roles have full access
    if (user.role === 'super_admin' || user.role === 'admin') return true;
    // Check granular permissions for tischplan feature
    const tischplanPermissions = user.permissions?.tischplan;
    return tischplanPermissions?.edit || tischplanPermissions?.admin;
  };
  const [tische, setTische] = useState([]);
  const [buchungen, setBuchungen] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateTischDialog, setShowCreateTischDialog] = useState(false);
  const [showCreateBuchungDialog, setShowCreateBuchungDialog] = useState(false);
  const [showEditBuchungDialog, setShowEditBuchungDialog] = useState(false);
  const [showDeleteBuchungDialog, setShowDeleteBuchungDialog] = useState(false);
  const [showBesonderheitenDialog, setShowBesonderheitenDialog] = useState(false);
  const [besonderheitenText, setBesonderheitenText] = useState('');
  const [selectedBuchung, setSelectedBuchung] = useState(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [tischForm, setTischForm] = useState({ tischnummer: '', kapazitaet: '' });
  const [buchungForm, setBuchungForm] = useState({
    typ: 'person',
    vorname: '',
    nachname: '',
    firma: '',
    tickets_erwartet: 1,
    tisch_id: ''
  });
  const [importData, setImportData] = useState([]);
  const [importErrors, setImportErrors] = useState({});
  const [bookingSearchTerm, setBookingSearchTerm] = useState('');
  const [showPublicViewDialog, setShowPublicViewDialog] = useState(false);
  const [publicViewUrl, setPublicViewUrl] = useState('');
  const [showSitzplatzImportDialog, setShowSitzplatzImportDialog] = useState(false);
  const [sitzplatzData, setSitzplatzData] = useState([]);
  const [sitzplatzResults, setSitzplatzResults] = useState(null);
  const [manualAssignments, setManualAssignments] = useState({});
  const [showSitzplatzUebersicht, setShowSitzplatzUebersicht] = useState(false);
  const [sitzplatzUebersichtData, setSitzplatzUebersichtData] = useState([]);

  useEffect(() => {
    if (eventId) {
      fetchEventData();
    }
  }, [eventId]);

  const fetchEventData = async () => {
    try {
      const [eventResponse, tischeResponse, buchungenResponse, statsResponse] = await Promise.all([
        axios.get(`/events/${eventId}`),
        axios.get(`/events/${eventId}/tische`),
        axios.get(`/events/${eventId}/buchungen`),
        axios.get(`/events/${eventId}/statistics`)
      ]);
      
      setEvent(eventResponse.data);
      
      // Sort tables numerically by tischnummer
      const sortedTische = tischeResponse.data.sort((a, b) => {
        const numA = parseInt(a.tischnummer) || 0;
        const numB = parseInt(b.tischnummer) || 0;
        if (numA !== numB) return numA - numB;
        // If both are numbers and equal, or if not numbers, sort alphabetically
        return a.tischnummer.localeCompare(b.tischnummer);
      });
      
      setTische(sortedTische);
      setBuchungen(buchungenResponse.data);
      setStatistics(statsResponse.data);
    } catch (error) {
      console.error('Error fetching event data:', error);
      toast.error('Fehler beim Laden der Event-Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTisch = async (e) => {
    e.preventDefault();
    
    if (!tischForm.tischnummer || !tischForm.kapazitaet) {
      toast.error('Bitte fuellen Sie alle Felder aus');
      return;
    }

    try {
      const response = await axios.post(`/events/${eventId}/tische`, {
        tischnummer: tischForm.tischnummer,
        kapazitaet: parseInt(tischForm.kapazitaet)
      });
      
      setTische([...tische, response.data]);
      setTischForm({ tischnummer: '', kapazitaet: '' });
      setShowCreateTischDialog(false);
      toast.success('Tisch erfolgreich erstellt!');
      fetchEventData();
    } catch (error) {
      console.error('Error creating table:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Tisches');
    }
  };

  const handleCreateBuchung = async (e) => {
    e.preventDefault();
    
    console.log('Buchung form data:', buchungForm);
    
    // Validation for Person type
    if (buchungForm.typ === 'person' && (!buchungForm.vorname || !buchungForm.nachname)) {
      toast.error('Bitte fuellen Sie Vor- und Nachname aus');
      return;
    }
    
    // Validation for Firma type
    if (buchungForm.typ === 'firma' && !buchungForm.firma) {
      toast.error('Bitte fuellen Sie den Firmennamen aus');
      return;
    }

    const payload = {
      ...buchungForm,
      tickets_erwartet: parseInt(buchungForm.tickets_erwartet),
      tisch_id: buchungForm.tisch_id || null
    };
    
    console.log('Sending payload:', payload);
    console.log('Event ID:', eventId);

    try {
      const response = await axios.post(`/events/${eventId}/buchungen`, payload);
      console.log('Response:', response.data);
      
      setBuchungen([...buchungen, response.data]);
      setBuchungForm({
        typ: 'person',
        vorname: '',
        nachname: '',
        firma: '',
        tickets_erwartet: 1,
        tisch_id: ''
      });
      setShowCreateBuchungDialog(false);
      toast.success('Buchung erfolgreich erstellt!');
      fetchEventData();
    } catch (error) {
      console.error('Error creating booking:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen der Buchung');
    }
  };

  // CSV Import Functions
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      parseCSV(text);
    };
    reader.readAsText(file);
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      toast.error('CSV-Datei ist leer');
      return;
    }

    // Detect delimiter (semicolon or comma)
    const firstLine = lines[0];
    const delimiter = firstLine.includes(';') ? ';' : ',';
    
    // Parse header
    const headers = lines[0].split(delimiter).map(h => h.trim().toLowerCase());
    
    // Parse data rows
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(delimiter).map(v => v.trim());
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      row._index = i - 1; // Store original index for editing
      data.push(row);
    }

    // Validate and set data
    const validatedData = data.map(row => ({
      ...row,
      vorname: row.vorname || '',
      nachname: row.nachname || '',
      firma: row.firma || '',
      anzahl_tickets: parseInt(row.anzahl_tickets || row['anzahl tickets'] || '1'),
      tischnummer: row.tischnummer || '',
      tags: row.tags || '',
      notiz: row.notiz || ''
    }));

    setImportData(validatedData);
    validateImportData(validatedData);
  };

  const validateImportData = (data) => {
    const errors = {};
    
    data.forEach((row, index) => {
      const rowErrors = [];
      
      // Determine type based on filled fields
      const isPerson = row.vorname && row.nachname;
      const isFirma = row.firma;
      
      if (!isPerson && !isFirma) {
        rowErrors.push('Entweder Vorname+Nachname oder Firma muss ausgefüllt sein');
      }
      
      if (isPerson && (!row.vorname || !row.nachname)) {
        rowErrors.push('Person benötigt Vor- und Nachname');
      }
      
      if (isFirma && !row.firma) {
        rowErrors.push('Firma benötigt Firmennamen');
      }
      
      if (!row.anzahl_tickets || row.anzahl_tickets < 1) {
        rowErrors.push('Anzahl Tickets muss mindestens 1 sein');
      }
      
      if (!row.tischnummer) {
        rowErrors.push('Tischnummer ist erforderlich');
      } else {
        // Check if table exists
        const tableExists = tische.some(t => t.tischnummer === row.tischnummer);
        if (!tableExists) {
          rowErrors.push(`Tisch "${row.tischnummer}" existiert nicht`);
        }
      }
      
      if (rowErrors.length > 0) {
        errors[index] = rowErrors;
      }
    });
    
    setImportErrors(errors);
  };

  const updateImportRow = (index, field, value) => {
    const newData = [...importData];
    newData[index][field] = value;
    setImportData(newData);
    validateImportData(newData);
  };

  const handleImport = async () => {
    // Check if there are any errors
    if (Object.keys(importErrors).length > 0) {
      toast.error('Bitte beheben Sie alle Fehler vor dem Import');
      return;
    }

    try {
      const bookingsToCreate = importData.map(row => {
        const tisch = tische.find(t => t.tischnummer === row.tischnummer);
        const isPerson = row.vorname && row.nachname;
        
        return {
          typ: isPerson ? 'person' : 'firma',
          vorname: row.vorname || null,
          nachname: row.nachname || null,
          firma: row.firma || null,
          tickets_erwartet: parseInt(row.anzahl_tickets),
          tisch_id: tisch?.id || null,
          tags: row.tags ? row.tags.split(';').map(t => t.trim()) : [],
          notiz: row.notiz || null
        };
      });

      // Import all bookings
      let successCount = 0;
      let errorCount = 0;
      
      for (const booking of bookingsToCreate) {
        try {
          await axios.post(`/events/${eventId}/buchungen`, booking);
          successCount++;
        } catch (error) {
          console.error('Error importing booking:', error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} Buchungen erfolgreich importiert${errorCount > 0 ? ` (${errorCount} Fehler)` : ''}`);
      }
      
      // Refresh data
      fetchEventData();
      
      // Close dialog
      setShowImportDialog(false);
      setImportData([]);
      setImportErrors({});
    } catch (error) {
      console.error('Error during import:', error);
      toast.error('Fehler beim Import');
    }
  };

  // Buchung bearbeiten
  const handleEditBuchung = (buchung) => {
    setSelectedBuchung(buchung);
    setBuchungForm({
      typ: buchung.typ,
      vorname: buchung.vorname || '',
      nachname: buchung.nachname || '',
      firma: buchung.firma || '',
      tickets_erwartet: buchung.tickets_erwartet,
      tisch_id: buchung.tisch_id || ''
    });
    setShowEditBuchungDialog(true);
  };

  const handleUpdateBuchung = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.patch(`/buchungen/${selectedBuchung.id}`, buchungForm);
      
      // Update buchung in list
      setBuchungen(buchungen.map(b => b.id === selectedBuchung.id ? response.data : b));
      
      setShowEditBuchungDialog(false);
      setSelectedBuchung(null);
      setBuchungForm({
        typ: 'person',
        vorname: '',
        nachname: '',
        firma: '',
        tickets_erwartet: 1,
        tisch_id: ''
      });
      
      toast.success('Buchung erfolgreich aktualisiert');
      fetchEventData(); // Refresh statistics
    } catch (error) {
      console.error('Error updating booking:', error);
      toast.error('Fehler beim Aktualisieren der Buchung');
    }
  };

  // Buchung löschen
  const handleDeleteBuchungClick = (buchung) => {
    setSelectedBuchung(buchung);
    setShowDeleteBuchungDialog(true);
  };

  const handleDeleteBuchung = async () => {
    if (!selectedBuchung) return;

    try {
      await axios.delete(`/buchungen/${selectedBuchung.id}`);
      
      // Remove buchung from list
      setBuchungen(buchungen.filter(b => b.id !== selectedBuchung.id));
      
      setShowDeleteBuchungDialog(false);
      setSelectedBuchung(null);
      
      toast.success('Buchung erfolgreich gelöscht');
      fetchEventData(); // Refresh statistics
    } catch (error) {
      console.error('Error deleting booking:', error);
      toast.error('Fehler beim Löschen der Buchung');
    }
  };

  const handleSaveBesonderheiten = async () => {
    try {
      await axios.patch(`/events/${eventId}`, {
        besonderheiten: besonderheitenText
      });
      
      // Update local event data
      setEvent({...event, besonderheiten: besonderheitenText});
      setShowBesonderheitenDialog(false);
      toast.success('Besonderheiten erfolgreich gespeichert');
    } catch (error) {
      console.error('Error saving besonderheiten:', error);
      toast.error('Fehler beim Speichern der Besonderheiten');
    }
  };

  const handleGeneratePublicLink = async () => {
    try {
      const response = await axios.post(`/events/${eventId}/generate-public-token`);
      const token = response.data.token;
      
      // Generate the full public URL
      const fullUrl = `${window.location.origin}/public/event/${token}`;
      setPublicViewUrl(fullUrl);
      
      // Update local event data
      setEvent({...event, public_view_token: token});
      
      setShowPublicViewDialog(true);
      toast.success('Öffentlicher Link generiert');
    } catch (error) {
      console.error('Error generating public link:', error);
      toast.error('Fehler beim Generieren des Links');
    }
  };

  const handleCopyPublicLink = () => {
    navigator.clipboard.writeText(publicViewUrl);
    toast.success('Link in die Zwischenablage kopiert');
  };

  const handleOpenPublicLink = () => {
    window.open(publicViewUrl, '_blank');
  };

  // Sitzplatz Übersicht
  const loadSitzplatzUebersicht = () => {
    // Create an array of seat entries (one per seat, linked to booking)
    const seatEntries = [];
    
    buchungen.forEach(buchung => {
      if (buchung.sitzplaetze && buchung.sitzplaetze.length > 0) {
        buchung.sitzplaetze.forEach(seat => {
          seatEntries.push({
            ...seat,
            buchung: buchung
          });
        });
      }
    });
    
    // Sort by block, then reihe, then platz
    const sorted = seatEntries.sort((a, b) => {
      if (a.block !== b.block) return a.block.localeCompare(b.block);
      
      const reiheA = parseInt(a.reihe) || 0;
      const reiheB = parseInt(b.reihe) || 0;
      if (reiheA !== reiheB) return reiheA - reiheB;
      
      const platzA = parseInt(a.platz) || 0;
      const platzB = parseInt(b.platz) || 0;
      return platzA - platzB;
    });
    
    setSitzplatzUebersichtData(sorted);
    setShowSitzplatzUebersicht(true);
  };

  // Sitzplatz Import Functions
  const handleSitzplatzFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      parseSitzplatzCSV(text);
    };
    reader.readAsText(file);
  };

  const parseSitzplatzCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      toast.error('CSV-Datei ist leer');
      return;
    }

    // Detect delimiter
    const firstLine = lines[0];
    const delimiter = firstLine.includes(';') ? ';' : ',';
    
    // Parse header
    const headers = lines[0].split(delimiter).map(h => h.trim().toLowerCase());
    
    // Parse data rows
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(delimiter).map(v => v.trim());
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      data.push(row);
    }

    setSitzplatzData(data);
    processSitzplaetze(data);
  };

  const processSitzplaetze = async (data) => {
    try {
      const response = await axios.post(`/events/${eventId}/process-sitzplaetze`, data);
      setSitzplatzResults(response.data);
      
      // Count statuses
      const results = response.data.results;
      const matched = results.filter(r => r.status === 'match_found').length;
      const noMatch = results.filter(r => r.status === 'no_match').length;
      const errors = results.filter(r => r.status === 'error' || r.status === 'already_assigned').length;
      
      toast.success(`${matched} Zuordnungen gefunden, ${noMatch} ohne Match, ${errors} Fehler`);
    } catch (error) {
      console.error('Error processing seats:', error);
      toast.error('Fehler beim Verarbeiten der Sitzplätze');
    }
  };

  const handleManualAssignment = (rowIndex, buchungId) => {
    setManualAssignments(prev => ({
      ...prev,
      [rowIndex]: buchungId
    }));
  };

  const handleConfirmSitzplaetze = async () => {
    if (!sitzplatzResults) return;

    try {
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      // Process in batches of 20 to avoid overwhelming the server
      const batchSize = 20;
      const results = sitzplatzResults.results;
      
      for (let i = 0; i < results.length; i += batchSize) {
        const batch = results.slice(i, i + batchSize);
        
        const promises = batch.map(async (result) => {
          if (result.status === 'error' || result.status === 'already_assigned') {
            return { success: false, reason: result.status };
          }

          // Use manual assignment if exists, otherwise use suggested
          const buchungId = manualAssignments[result.row_index] 
            || result.suggested_buchung?.id;

          if (!buchungId) {
            return { success: false, reason: 'no_booking' };
          }

          try {
            await axios.post(`/events/${eventId}/assign-sitzplatz`, {
              buchung_id: buchungId,
              block: result.seat_data.block,
              reihe: result.seat_data.reihe,
              platz: result.seat_data.platz
            });
            return { success: true };
          } catch (err) {
            console.error('Error assigning seat:', err);
            return { 
              success: false, 
              reason: 'api_error',
              error: err.response?.data?.detail || err.message,
              seat: `${result.seat_data.block}-${result.seat_data.reihe}-${result.seat_data.platz}`
            };
          }
        });

        const batchResults = await Promise.all(promises);
        
        batchResults.forEach((res) => {
          if (res.success) {
            successCount++;
          } else {
            errorCount++;
            if (res.error) {
              errors.push(res);
            }
          }
        });
      }

      if (errors.length > 0) {
        console.error('Failed assignments:', errors);
      }

      toast.success(`${successCount} Sitzplätze zugewiesen${errorCount > 0 ? `, ${errorCount} Fehler` : ''}`);
      
      // Reset and close
      setSitzplatzData([]);
      setSitzplatzResults(null);
      setManualAssignments({});
      setShowSitzplatzImportDialog(false);
      
      // Refresh data
      fetchEventData();
    } catch (error) {
      console.error('Error confirming seats:', error);
      toast.error('Fehler beim Zuweisen der Sitzplätze');
    }
  };

  const getBuchungTypLabel = (typ) => {
    switch(typ) {
      case 'person': return 'Person';
      case 'firma': return 'Firma';
      default: return typ;
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'gar_nicht_da':
        return <Badge className="bg-gray-100 text-gray-700">Nicht da</Badge>;
      case 'teilweise_da':
        return <Badge className="bg-amber-100 text-amber-700">Teilweise da</Badge>;
      case 'vollstaendig_da':
        return <Badge className="bg-green-100 text-green-700">Vollstaendig da</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700">Unbekannt</Badge>;
    }
  };

  const getTischByBuchung = (buchung) => {
    if (!buchung.tisch_id) return null;
    return tische.find(t => t.id === buchung.tisch_id);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Event nicht gefunden</h2>
          <Button onClick={() => navigate('/events')}>Zurueck zur Uebersicht</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-950 dark:to-black">
      {/* Navigation Header */}
      <div className="bg-white dark:bg-gray-900 shadow-sm border-b dark:border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/events')}
                className="flex items-center space-x-2"
                data-testid="back-to-events-button"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Events</span>
              </Button>
              <div>
                <h1 className="text-xl font-bold font-space-grotesk text-gray-900">
                  {event.name}
                </h1>
                <p className="text-sm text-gray-600">
                  {format(new Date(event.datum), 'dd.MM.yyyy HH:mm', { locale: de })}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Link to={`/events/${eventId}/checkin`}>
                <Button 
                  className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
                  data-testid="open-checkin-button"
                >
                  <Smartphone className="w-4 h-4 mr-2" />
                  Check-In starten
                </Button>
              </Link>
              
              {canManageTischplan() && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setBesonderheitenText(event.besonderheiten || '');
                      setShowBesonderheitenDialog(true);
                    }}
                    className="flex items-center space-x-2 hover:bg-purple-50 hover:text-purple-600 hover:border-purple-200"
                  >
                    <FileText className="w-4 h-4" />
                    <span>Besonderheiten</span>
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={handleGeneratePublicLink}
                    className="flex items-center space-x-2 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200"
                  >
                    <Eye className="w-4 h-4" />
                    <span>Öffentlicher Link</span>
                  </Button>
                </>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={onLogout}
                className="flex items-center space-x-2 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                <span>Abmelden</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="card-glass animate-fade-in">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-gray-600">Tische gesamt</CardTitle>
                  <Table className="w-5 h-5 text-blue-600" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">{statistics.overall.tables_count}</div>
              </CardContent>
            </Card>

            <Card className="card-glass animate-fade-in" style={{ animationDelay: '50ms' }}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-gray-600">Buchungen</CardTitle>
                  <Users className="w-5 h-5 text-emerald-600" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">{statistics.overall.bookings_count}</div>
                {statistics.overall.unassigned_bookings > 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    {statistics.overall.unassigned_bookings} ohne Tisch
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="card-glass animate-fade-in" style={{ animationDelay: '100ms' }}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-gray-600">Erwartet / Kapazitaet</CardTitle>
                  <BarChart3 className="w-5 h-5 text-orange-600" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">
                  {statistics.overall.total_expected} / {statistics.overall.total_capacity}
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  {((statistics.overall.total_expected / statistics.overall.total_capacity) * 100).toFixed(1)}% Auslastung
                </p>
              </CardContent>
            </Card>

            <Card className="card-glass animate-fade-in" style={{ animationDelay: '150ms' }}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-gray-600">Anwesend</CardTitle>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">{statistics.overall.total_present}</div>
                <p className="text-xs text-gray-600 mt-1">
                  von {statistics.overall.total_expected} erwartet
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Actions Section */}
        {canManageTischplan() && (
          <div className="flex flex-wrap gap-4 mb-8">
            <Dialog open={showCreateTischDialog} onOpenChange={setShowCreateTischDialog}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
                  data-testid="create-table-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Tisch hinzufuegen
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[400px]">
                <DialogHeader>
                  <DialogTitle>Neuen Tisch erstellen</DialogTitle>
                  <DialogDescription>
                    Fuegen Sie einen neuen Tisch zu diesem Event hinzu.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateTisch} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="tischnummer">Tischnummer *</Label>
                    <Input
                      id="tischnummer"
                      value={tischForm.tischnummer}
                      onChange={(e) => setTischForm({ ...tischForm, tischnummer: e.target.value })}
                      placeholder="z.B. 12, VIP-1, etc."
                      required
                      data-testid="table-number-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="kapazitaet">Kapazitaet *</Label>
                    <Input
                      id="kapazitaet"
                      type="number"
                      min="1"
                      value={tischForm.kapazitaet}
                      onChange={(e) => setTischForm({ ...tischForm, kapazitaet: e.target.value })}
                      placeholder="Anzahl Plaetze"
                      required
                      data-testid="table-capacity-input"
                    />
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowCreateTischDialog(false)}
                    >
                      Abbrechen
                    </Button>
                    <Button type="submit" data-testid="submit-create-table-button">
                      Tisch erstellen
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={showCreateBuchungDialog} onOpenChange={setShowCreateBuchungDialog}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
                  data-testid="create-booking-button"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Buchung hinzufuegen
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>Neue Buchung erstellen</DialogTitle>
                  <DialogDescription>
                    Fuegen Sie eine neue Buchung zu diesem Event hinzu.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateBuchung} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="typ">Buchungstyp *</Label>
                    <select
                      value={buchungForm.typ}
                      onChange={(e) => setBuchungForm({ ...buchungForm, typ: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-md"
                      data-testid="booking-type-select"
                    >
                      <option value="person">Person</option>
                      <option value="firma">Firma</option>
                    </select>
                  </div>
                  
                  {buchungForm.typ === 'person' && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="vorname">Vorname *</Label>
                          <Input
                            id="vorname"
                            value={buchungForm.vorname}
                            onChange={(e) => setBuchungForm({ ...buchungForm, vorname: e.target.value })}
                            required
                            data-testid="booking-firstname-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="nachname">Nachname *</Label>
                          <Input
                            id="nachname"
                            value={buchungForm.nachname}
                            onChange={(e) => setBuchungForm({ ...buchungForm, nachname: e.target.value })}
                            required
                            data-testid="booking-lastname-input"
                          />
                        </div>
                      </div>
                    </>
                  )}
                  
                  {buchungForm.typ === 'firma' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="firma">Firmenname *</Label>
                        <Input
                          id="firma"
                          value={buchungForm.firma}
                          onChange={(e) => setBuchungForm({ ...buchungForm, firma: e.target.value })}
                          required
                          data-testid="booking-company-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="vorname-optional">Vorname (Optional)</Label>
                          <Input
                            id="vorname-optional"
                            value={buchungForm.vorname}
                            onChange={(e) => setBuchungForm({ ...buchungForm, vorname: e.target.value })}
                            data-testid="booking-firstname-optional-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="nachname-optional">Nachname (Optional)</Label>
                          <Input
                            id="nachname-optional"
                            value={buchungForm.nachname}
                            onChange={(e) => setBuchungForm({ ...buchungForm, nachname: e.target.value })}
                            data-testid="booking-lastname-optional-input"
                          />
                        </div>
                      </div>
                    </>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="tickets_erwartet">Erwartete Tickets</Label>
                    <Input
                      id="tickets_erwartet"
                      type="number"
                      min="1"
                      value={buchungForm.tickets_erwartet}
                      onChange={(e) => setBuchungForm({ ...buchungForm, tickets_erwartet: e.target.value })}
                      data-testid="booking-tickets-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="tisch_id">Tisch (optional)</Label>
                    <select
                      value={buchungForm.tisch_id}
                      onChange={(e) => setBuchungForm({ ...buchungForm, tisch_id: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-md"
                      data-testid="booking-table-select"
                    >
                      <option value="">Kein Tisch</option>
                      {tische.map(tisch => (
                        <option key={tisch.id} value={tisch.id}>
                          Tisch {tisch.tischnummer} (Kapazitaet: {tisch.kapazitaet})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowCreateBuchungDialog(false)}
                    >
                      Abbrechen
                    </Button>
                    <Button type="submit" data-testid="submit-create-booking-button">
                      Buchung erstellen
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {canManageTischplan() && (
              <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 text-white"
                    data-testid="import-bookings-button"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Buchungen importieren
                  </Button>
                </DialogTrigger>
              <DialogContent className="sm:max-w-[800px] max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Buchungen aus CSV importieren</DialogTitle>
                  <DialogDescription>
                    Laden Sie eine CSV-Datei hoch, um mehrere Buchungen gleichzeitig zu erstellen.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                  {importData.length === 0 ? (
                    <div className="space-y-4">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">CSV-Datei hochladen</h3>
                        <p className="text-gray-600 mb-4">
                          Wählen Sie eine CSV-Datei mit Buchungsdaten aus.
                        </p>
                        <input
                          type="file"
                          accept=".csv"
                          onChange={handleFileUpload}
                          className="hidden"
                          id="csv-upload"
                        />
                        <label
                          htmlFor="csv-upload"
                          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Datei auswählen
                        </label>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-2">CSV-Format:</h4>
                        <p className="text-sm text-gray-600 mb-2">
                          Die CSV-Datei sollte folgende Spalten enthalten:
                        </p>
                        <ul className="text-sm text-gray-600 space-y-1">
                          <li>• <strong>vorname</strong> - Vorname (für Einzelgäste)</li>
                          <li>• <strong>nachname</strong> - Nachname (für Einzelgäste)</li>
                          <li>• <strong>firma</strong> - Firmenname (für Firmenkontingente)</li>
                          <li>• <strong>anzahl_tickets</strong> - Erwartete Anzahl Tickets</li>
                          <li>• <strong>tischnummer</strong> - Tischnummer (muss existieren)</li>
                          <li>• <strong>tags</strong> - Tags (optional, getrennt durch Semikolon)</li>
                          <li>• <strong>notiz</strong> - Notiz (optional)</li>
                        </ul>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-gray-900">
                          Importdaten ({importData.length} Einträge)
                        </h4>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setImportData([]);
                              setImportErrors({});
                            }}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Zurücksetzen
                          </Button>
                          <Button
                            onClick={handleImport}
                            disabled={Object.keys(importErrors).length > 0}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Importieren
                          </Button>
                        </div>
                      </div>
                      
                      {Object.keys(importErrors).length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                          <p className="text-sm text-red-800 font-medium">
                            {Object.keys(importErrors).length} Fehler gefunden. Bitte korrigieren Sie diese vor dem Import.
                          </p>
                        </div>
                      )}
                      
                      <div className="max-h-96 overflow-y-auto border rounded-lg">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-3 py-2 text-left">#</th>
                              <th className="px-3 py-2 text-left">Name/Firma</th>
                              <th className="px-3 py-2 text-left">Tickets</th>
                              <th className="px-3 py-2 text-left">Tisch</th>
                              <th className="px-3 py-2 text-left">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {importData.map((row, index) => (
                              <tr key={index} className={`border-t ${importErrors[index] ? 'bg-red-50' : ''}`}>
                                <td className="px-3 py-2">{index + 1}</td>
                                <td className="px-3 py-2">
                                  {row.vorname && row.nachname ? (
                                    <div>
                                      <input
                                        type="text"
                                        value={row.vorname}
                                        onChange={(e) => updateImportRow(index, 'vorname', e.target.value)}
                                        className="w-full mb-1 px-2 py-1 border rounded text-xs"
                                        placeholder="Vorname"
                                      />
                                      <input
                                        type="text"
                                        value={row.nachname}
                                        onChange={(e) => updateImportRow(index, 'nachname', e.target.value)}
                                        className="w-full px-2 py-1 border rounded text-xs"
                                        placeholder="Nachname"
                                      />
                                    </div>
                                  ) : (
                                    <input
                                      type="text"
                                      value={row.firma}
                                      onChange={(e) => updateImportRow(index, 'firma', e.target.value)}
                                      className="w-full px-2 py-1 border rounded text-xs"
                                      placeholder="Firmenname"
                                    />
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  <input
                                    type="number"
                                    value={row.anzahl_tickets}
                                    onChange={(e) => updateImportRow(index, 'anzahl_tickets', parseInt(e.target.value) || 1)}
                                    className="w-full px-2 py-1 border rounded text-xs"
                                    min="1"
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <select
                                    value={row.tischnummer}
                                    onChange={(e) => updateImportRow(index, 'tischnummer', e.target.value)}
                                    className="w-full px-2 py-1 border rounded text-xs"
                                  >
                                    <option value="">Tisch wählen</option>
                                    {tische.map(tisch => (
                                      <option key={tisch.id} value={tisch.tischnummer}>
                                        {tisch.tischnummer}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td className="px-3 py-2">
                                  {importErrors[index] ? (
                                    <div className="text-red-600">
                                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                                      <span className="text-xs">Fehler</span>
                                      <div className="text-xs mt-1">
                                        {importErrors[index].map((error, i) => (
                                          <div key={i}>• {error}</div>
                                        ))}
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="text-green-600">
                                      <Check className="w-4 h-4 inline mr-1" />
                                      <span className="text-xs">OK</span>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            )}
            
            {canManageTischplan() && (
              <>
                <Button 
                  onClick={loadSitzplatzUebersicht}
                  className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white"
                >
                  <Table className="w-4 h-4 mr-2" />
                  Sitzplatz-Übersicht
                </Button>
                
                <Dialog open={showSitzplatzImportDialog} onOpenChange={setShowSitzplatzImportDialog}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white"
                    data-testid="import-seats-button"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Sitzplätze importieren
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[900px] max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Sitzplätze aus CSV importieren</DialogTitle>
                    <DialogDescription>
                      Laden Sie eine CSV-Datei mit Sitzplatzdaten hoch. Das System ordnet automatisch die passenden Buchungen zu.
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4 mt-4">
                    {!sitzplatzResults ? (
                      <div className="space-y-4">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">CSV-Datei hochladen</h3>
                          <p className="text-gray-600 mb-4">
                            Wählen Sie eine CSV-Datei mit Sitzplatzdaten aus.
                          </p>
                          <input
                            type="file"
                            accept=".csv"
                            onChange={handleSitzplatzFileUpload}
                            className="hidden"
                            id="sitzplatz-csv-upload"
                          />
                          <label
                            htmlFor="sitzplatz-csv-upload"
                            className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 cursor-pointer"
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Datei auswählen
                          </label>
                        </div>
                        
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-semibold text-gray-900 mb-2">CSV-Format:</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Die CSV-Datei sollte folgende Spalten enthalten:
                          </p>
                          <ul className="text-sm text-gray-600 space-y-1">
                            <li>• <strong>block</strong> - Tribünenblock (z.B. A, B, C)</li>
                            <li>• <strong>reihe</strong> - Reihennummer</li>
                            <li>• <strong>platz</strong> - Platznummer</li>
                            <li>• <strong>firma</strong> - Firmenname (optional)</li>
                            <li>• <strong>vorname</strong> - Vorname (optional)</li>
                            <li>• <strong>nachname</strong> - Nachname (optional)</li>
                          </ul>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-gray-900">
                            Zuordnungsergebnisse ({sitzplatzResults.total_rows} Sitzplätze)
                          </h4>
                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSitzplatzData([]);
                                setSitzplatzResults(null);
                                setManualAssignments({});
                              }}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Zurücksetzen
                            </Button>
                            <Button
                              onClick={handleConfirmSitzplaetze}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Zuordnungen bestätigen
                            </Button>
                          </div>
                        </div>
                        
                        <div className="max-h-96 overflow-y-auto border rounded-lg">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="px-3 py-2 text-left">#</th>
                                <th className="px-3 py-2 text-left">Sitzplatz</th>
                                <th className="px-3 py-2 text-left">CSV-Daten</th>
                                <th className="px-3 py-2 text-left">Zuordnung</th>
                                <th className="px-3 py-2 text-left">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sitzplatzResults.results.map((result, index) => (
                                <tr key={index} className="border-t">
                                  <td className="px-3 py-2">{index + 1}</td>
                                  <td className="px-3 py-2">
                                    <div className="font-medium">
                                      Block {result.seat_data.block}, Reihe {result.seat_data.reihe}, Platz {result.seat_data.platz}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2">
                                    <div className="text-xs">
                                      {result.seat_data.firma && <div><strong>Firma:</strong> {result.seat_data.firma}</div>}
                                      {result.seat_data.vorname && <div><strong>Name:</strong> {result.seat_data.vorname} {result.seat_data.nachname}</div>}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2">
                                    {result.status === 'match_found' || result.status === 'no_match' ? (
                                      <select
                                        value={manualAssignments[result.row_index] || result.suggested_buchung?.id || ''}
                                        onChange={(e) => handleManualAssignment(result.row_index, e.target.value)}
                                        className="w-full px-2 py-1 border rounded text-xs"
                                      >
                                        {result.status === 'no_match' && <option value="">Bitte wählen...</option>}
                                        {[...sitzplatzResults.all_buchungen]
                                          .sort((a, b) => {
                                            const nameA = (a.firma || `${a.nachname}, ${a.vorname}`).toLowerCase();
                                            const nameB = (b.firma || `${b.nachname}, ${b.vorname}`).toLowerCase();
                                            return nameA.localeCompare(nameB);
                                          })
                                          .map(buchung => {
                                            const hasSeats = buchung.block && buchung.reihe && buchung.platz;
                                            const displayName = buchung.firma || `${buchung.nachname}, ${buchung.vorname}`;
                                            return (
                                              <option 
                                                key={buchung.id} 
                                                value={buchung.id}
                                                disabled={hasSeats && !manualAssignments[result.row_index]}
                                              >
                                                {displayName} {hasSeats ? '(bereits zugeordnet)' : ''}
                                              </option>
                                            );
                                          })}
                                      </select>
                                    ) : (
                                      <span className="text-xs text-gray-500">{result.message}</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2">
                                    {result.status === 'match_found' && (
                                      <Badge className="bg-green-100 text-green-700 text-xs">
                                        Match {result.confidence}%
                                      </Badge>
                                    )}
                                    {result.status === 'no_match' && (
                                      <Badge className="bg-yellow-100 text-yellow-700 text-xs">
                                        Kein Match
                                      </Badge>
                                    )}
                                    {result.status === 'error' && (
                                      <Badge className="bg-red-100 text-red-700 text-xs">
                                        Fehler
                                      </Badge>
                                    )}
                                    {result.status === 'already_assigned' && (
                                      <Badge className="bg-gray-100 text-gray-700 text-xs">
                                        Bereits vergeben
                                      </Badge>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
              </>
            )}
          </div>
        )}

        {/* Tables and Bookings Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Tables */}
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Tische</h2>
              <Badge className="bg-blue-100 text-blue-700">
                {tische.length} Tisch{tische.length !== 1 ? 'e' : ''}
              </Badge>
            </div>
            
            {tische.length === 0 ? (
              <Card className="card text-center py-8" data-testid="no-tables-message">
                <CardContent>
                  <Table className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Keine Tische vorhanden</h3>
                  <p className="text-gray-600 mb-4">Erstellen Sie Ihren ersten Tisch fuer dieses Event.</p>
                  {canManageTischplan() && (
                    <Button 
                      onClick={() => setShowCreateTischDialog(true)}
                      className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Ersten Tisch erstellen
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {tische.map((tisch, index) => {
                  const tischStats = statistics?.table_statistics?.find(s => s.tisch_id === tisch.id);
                  const isOverbooked = tischStats?.ueberbucht || false;
                  
                  return (
                    <Card 
                      key={tisch.id}
                      className={`card animate-slide-in ${isOverbooked ? 'border-l-4 border-l-red-500' : ''}`}
                      style={{ animationDelay: `${index * 50}ms` }}
                      data-testid={`table-card-${index}`}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg font-semibold text-gray-900">
                            Tisch {tisch.tischnummer}
                          </CardTitle>
                          <div className="flex items-center space-x-2">
                            {isOverbooked && (
                              <Badge className="bg-red-100 text-red-700 flex items-center">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Ueberbucht
                              </Badge>
                            )}
                            <Badge className="bg-gray-100 text-gray-700">
                              Kapazitaet: {tisch.kapazitaet}
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {tischStats && (
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <p className="text-gray-600">Erwartet</p>
                              <p className="font-semibold text-gray-900">{tischStats.tickets_erwartet}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Anwesend</p>
                              <p className="font-semibold text-gray-900">{tischStats.tickets_anwesend}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Frei</p>
                              <p className={`font-semibold ${tischStats.freie_plaetze < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                {tischStats.freie_plaetze}
                              </p>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Bookings */}
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Buchungen</h2>
              <Badge className="bg-emerald-100 text-emerald-700">
                {buchungen.length} Buchung{buchungen.length !== 1 ? 'en' : ''}
              </Badge>
            </div>
            
            {/* Search Field */}
            {buchungen.length > 0 && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  placeholder="Nach Name oder Firma suchen..."
                  value={bookingSearchTerm}
                  onChange={(e) => setBookingSearchTerm(e.target.value)}
                  className="pl-10 text-base py-2 bg-white shadow-sm border-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
            )}
            
            {buchungen.length === 0 ? (
              <Card className="card text-center py-8" data-testid="no-bookings-message">
                <CardContent>
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Keine Buchungen vorhanden</h3>
                  <p className="text-gray-600 mb-4">Erstellen Sie Ihre erste Buchung fuer dieses Event.</p>
                  {canManageTischplan() && (
                    <Button 
                      onClick={() => setShowCreateBuchungDialog(true)}
                      className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Erste Buchung erstellen
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[600px] overflow-y-auto pr-2">
                {buchungen
                  .filter(buchung => {
                    if (!bookingSearchTerm) return true;
                    const search = bookingSearchTerm.toLowerCase();
                    return (
                      (buchung.vorname && buchung.vorname.toLowerCase().includes(search)) ||
                      (buchung.nachname && buchung.nachname.toLowerCase().includes(search)) ||
                      (buchung.firma && buchung.firma.toLowerCase().includes(search))
                    );
                  })
                  .map((buchung, index) => {
                  const assignedTisch = getTischByBuchung(buchung);
                  
                  return (
                    <Card 
                      key={buchung.id}
                      className="card animate-slide-in"
                      style={{ animationDelay: `${index * 30}ms` }}
                      data-testid={`booking-card-${index}`}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div className="min-w-0 flex-1">
                            <CardTitle className="text-base font-semibold text-gray-900 truncate">
                              {buchung.firma 
                                ? buchung.firma
                                : `${buchung.nachname}, ${buchung.vorname}`
                              }
                            </CardTitle>
                            <div className="flex items-center space-x-2 mt-1">
                              <Badge className="bg-blue-100 text-blue-700 text-xs">
                                {getBuchungTypLabel(buchung.typ)}
                              </Badge>
                              {getStatusBadge(buchung.status)}
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-2">
                        <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                          <div>
                            <p className="text-gray-600">Erwartet / Anwesend</p>
                            <p className="font-semibold text-gray-900">
                              {buchung.tickets_anwesend} / {buchung.tickets_erwartet}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">Tisch</p>
                            <p className="font-semibold text-gray-900">
                              {assignedTisch ? `Tisch ${assignedTisch.tischnummer}` : 'Nicht zugewiesen'}
                            </p>
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        {canManageTischplan() && (
                          <div className="flex items-center space-x-2 pt-3 border-t">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditBuchung(buchung)}
                              className="flex-1 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200"
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Bearbeiten
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteBuchungClick(buchung)}
                              className="flex-1 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Loeschen
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Buchung Dialog */}
      <Dialog open={showEditBuchungDialog} onOpenChange={setShowEditBuchungDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Buchung bearbeiten</DialogTitle>
            <DialogDescription>
              Aendern Sie die Buchungsdetails
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateBuchung} className="space-y-4">
            {/* Booking Type */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Buchungstyp</label>
              <select
                value={buchungForm.typ}
                onChange={(e) => setBuchungForm({...buchungForm, typ: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="person">Person</option>
                <option value="firma">Firma</option>
              </select>
            </div>

            {/* Person fields */}
            {buchungForm.typ === 'person' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Vorname *</label>
                  <input
                    type="text"
                    value={buchungForm.vorname}
                    onChange={(e) => setBuchungForm({...buchungForm, vorname: e.target.value})}
                    required
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Nachname *</label>
                  <input
                    type="text"
                    value={buchungForm.nachname}
                    onChange={(e) => setBuchungForm({...buchungForm, nachname: e.target.value})}
                    required
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            )}

            {/* Firma fields */}
            {buchungForm.typ === 'firma' && (
              <>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Firma *</label>
                  <input
                    type="text"
                    value={buchungForm.firma}
                    onChange={(e) => setBuchungForm({...buchungForm, firma: e.target.value})}
                    required
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Vorname (Optional)</label>
                    <input
                      type="text"
                      value={buchungForm.vorname}
                      onChange={(e) => setBuchungForm({...buchungForm, vorname: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Nachname (Optional)</label>
                    <input
                      type="text"
                      value={buchungForm.nachname}
                      onChange={(e) => setBuchungForm({...buchungForm, nachname: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Tickets count */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Anzahl Tickets</label>
              <input
                type="number"
                min="1"
                value={buchungForm.tickets_erwartet}
                onChange={(e) => setBuchungForm({...buchungForm, tickets_erwartet: parseInt(e.target.value) || 1})}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>

            {/* Table selection */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Tisch</label>
              <select
                value={buchungForm.tisch_id}
                onChange={(e) => setBuchungForm({...buchungForm, tisch_id: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="">Nicht zugewiesen</option>
                {tische.map(tisch => (
                  <option key={tisch.id} value={tisch.id}>
                    Tisch {tisch.tischnummer} (Kapazität: {tisch.kapazitaet})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowEditBuchungDialog(false);
                  setSelectedBuchung(null);
                }}
              >
                Abbrechen
              </Button>
              <Button 
                type="submit" 
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Speichern
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Buchung Dialog */}
      <Dialog open={showDeleteBuchungDialog} onOpenChange={setShowDeleteBuchungDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Buchung loeschen</DialogTitle>
            <DialogDescription>
              Moechten Sie diese Buchung wirklich loeschen? Diese Aktion kann nicht rueckgaengig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          {selectedBuchung && (
            <div className="py-4">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-1">
                  {selectedBuchung.firma 
                    ? selectedBuchung.firma
                    : `${selectedBuchung.nachname}, ${selectedBuchung.vorname}`
                  }
                </h4>
                <p className="text-sm text-gray-600">
                  {selectedBuchung.tickets_erwartet} Tickets erwartet
                </p>
              </div>
            </div>
          )}
          <div className="flex justify-end space-x-2 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setShowDeleteBuchungDialog(false);
                setSelectedBuchung(null);
              }}
            >
              Abbrechen
            </Button>
            <Button 
              onClick={handleDeleteBuchung}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Loeschen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Besonderheiten Dialog (Admin) */}
      <Dialog open={showBesonderheitenDialog} onOpenChange={setShowBesonderheitenDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Besonderheiten für Check-In</DialogTitle>
            <DialogDescription>
              Wichtige Hinweise für das Check-In-Personal
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Hinweise und Besonderheiten
              </label>
              <textarea
                value={besonderheitenText}
                onChange={(e) => setBesonderheitenText(e.target.value)}
                placeholder="z.B. VIP-Gäste erhalten Geschenk, Tisch 5 hat Allergie-Info..."
                rows={8}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowBesonderheitenDialog(false);
                  setBesonderheitenText(event.besonderheiten || '');
                }}
              >
                Abbrechen
              </Button>
              <Button 
                onClick={handleSaveBesonderheiten}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Public View Link Dialog (Admin) */}
      <Dialog open={showPublicViewDialog} onOpenChange={setShowPublicViewDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Öffentlicher Ansichtslink</DialogTitle>
            <DialogDescription>
              Teilen Sie diesen Link mit Ihrem Personal für eine passwortlose, schreibgeschützte Ansicht aller Buchungen
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 mb-3 font-medium">
                ℹ️ Dieser Link ermöglicht jedem mit Zugriff darauf, alle Buchungen und deren Status einzusehen. Keine Anmeldung erforderlich.
              </p>
              <div className="flex items-center space-x-2">
                <Input
                  value={publicViewUrl}
                  readOnly
                  className="flex-1 bg-white font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyPublicLink}
                  className="flex items-center space-x-1"
                >
                  <Copy className="w-4 h-4" />
                  <span>Kopieren</span>
                </Button>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowPublicViewDialog(false)}
              >
                Schließen
              </Button>
              <Button 
                onClick={handleOpenPublicLink}
                className="bg-blue-600 hover:bg-blue-700 text-white flex items-center space-x-2"
              >
                <Eye className="w-4 h-4" />
                <span>Link öffnen</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sitzplatz-Übersicht Dialog (Admin) */}
      <Dialog open={showSitzplatzUebersicht} onOpenChange={setShowSitzplatzUebersicht}>
        <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sitzplatz-Übersicht</DialogTitle>
            <DialogDescription>
              Alle zugeordneten Sitzplätze mit den entsprechenden Buchungen
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {sitzplatzUebersichtData.length === 0 ? (
              <div className="text-center py-8">
                <Table className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Keine Sitzplätze zugeordnet
                </h3>
                <p className="text-gray-600">
                  Es wurden noch keine Sitzplätze zu Buchungen zugeordnet.
                </p>
              </div>
            ) : (
              <>
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                  <p className="text-sm text-teal-800 font-medium">
                    📊 {sitzplatzUebersichtData.length} Sitzplätze zugeordnet
                  </p>
                </div>
                
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Block</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Reihe</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Platz</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Name/Firma</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Tisch</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Tickets</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {sitzplatzUebersichtData.map((seatEntry, index) => {
                        const buchung = seatEntry.buchung;
                        const tisch = tische.find(t => t.id === buchung.tisch_id);
                        const displayName = buchung.firma || `${buchung.nachname}, ${buchung.vorname}`;
                        
                        return (
                          <tr key={`${buchung.id}-${seatEntry.block}-${seatEntry.reihe}-${seatEntry.platz}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-4 py-3 font-medium text-gray-900">{seatEntry.block}</td>
                            <td className="px-4 py-3 text-gray-700">{seatEntry.reihe}</td>
                            <td className="px-4 py-3 text-gray-700">{seatEntry.platz}</td>
                            <td className="px-4 py-3">
                              <div>
                                <div className="font-medium text-gray-900">{displayName}</div>
                                {buchung.firma && buchung.vorname && (
                                  <div className="text-xs text-gray-600">
                                    {buchung.vorname} {buchung.nachname}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              {tisch ? `Tisch ${tisch.tischnummer}` : '-'}
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              {buchung.tickets_anwesend} / {buchung.tickets_erwartet}
                            </td>
                            <td className="px-4 py-3">
                              {getStatusBadge(buchung.status)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
          
          <div className="flex justify-end pt-4">
            <Button 
              onClick={() => setShowSitzplatzUebersicht(false)}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              Schließen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventDetails;